<template>
	<div class="data-table">
		<pagination
			:count="pagination.count"
			:page="pagination.page"
			:per_page="per_page"
			v-on:page="goToPage"
			:bottom="false"
		></pagination>
		<div class='search-box' v-if="searchable">
			<input class="uk-input" type="text" :placeholder="search_placeholder" v-model="search_term" />
		</div>
		<table ref="table" cellspacing="0" cellpadding="0">
			<thead>
				<tr>
                    <th v-if="multi_select">
                        <input ref="chbox" type="checkbox" class="uk-checkbox" v-model="allSelected" />
                    </th>
					<th v-for="(header,field) in table_headers"
						:class="headerClass(field,header)"
						:key="field"
						:style="header.style"
						@click="sortBy(header, field)">
							<span v-html="columnHeaderDisplay(header,field)" />
							<i class='fa fa-sort-down' v-if="sorting.current == field || sorting.current == table_headers[field].sortBy"></i>
					</th>
					<th v-if="useActions"></th>
				</tr>
			</thead>
			<tbody>
				<!-- data rows -->
				<tr v-if="!isLoading" v-for="(row,index) in list_rows"
					:class="rowClass(row)"
					:key="index">
                        <DataTableSelect v-if="multi_select"
                                         :key="row.id + 'dts'"
                                         :cell_data="row"
                                         :selected_ids="selectedRows"
                                         :id_field="actionIdField"
                                         :all_checked="allSelected"
                        />
						<DataTableCell v-for="(header,field) in table_headers" v-on="$listeners"
							:field="field"
							:key="field"
							:header="header"
							:cell_data="row"
							:sorted="sorting.current == field || sorting.current == table_headers[field].sortBy"
						/>
						<DataTableActions v-if="useActions"
							:key="index + 'dta'"
							:cell_data="row"
							:action_data="actions"
							:id_field="actionIdField"/>

				</tr>
				<!-- no data is available for table -->
				<tr v-if="list_rows.length == 0 && !isLoading" >
					<td :colspan="totalHeaders">{{ no_data }}</td>
				</tr>
                <tr v-if="isLoading" >
                    <td :colspan="totalHeaders"><img style="width: 18px;" src="/images/spinner2.gif" /> Loading...</td>
                </tr>
			</tbody>
		</table>
		<pagination
			:count="pagination.count"
			:page="pagination.page"
			:per_page="per_page"
			v-on:page="goToPage"
			:bottom="true"
		></pagination>
	</div>
</template>

<script>
// import any required components
import Pagination from './Pagination.vue';
import DataTableCell from './DataTableCell.vue';
import DataTableActions from './DataTableActions.vue';
import DataTableSelect from './DataTableSelect.vue';
import GlobalStorage from "../../Classes/GlobalStorage";
import _ from 'lodash';
// sortby
import _sortBy from 'lodash/sortBy';

// export component
export default {
// properties that the component will receive from parent
	props: {
		/**
		 * data to use for table
		 * @type {Object}
		 */
		table_data:{
			type: Array,
			default: () => []
		},
		/**
		 * Api point for data requests (will be prefixed with /api/)
		 */
		api_point:{
			type: String,
			default: ""
		},
		/**
		 * table headers and settings for each cell
		 * @type {Object}
		 */
		table_headers:{
			type: Object,
			default: () => {}
		},
		/**
		 * search field placeholder string
		 * @type {Object}
		 */
		search_placeholder: {
			type: String,
			default: "Quick Search"
		},
		/**
		 * whether table is searchable or not
		 * will hide search field if set to false
		 * @type {Boolean}
		 */
		searchable: {
			type: Boolean,
			default: true
		},
        size_content: {
			type: Boolean,
			default: false
		},
		/**
		 * how many items to display per page
		 * @type {Object}
		 */
		per_page:{
			type: Number,
			default: 30
		},
		/**
		 * string to display if no items are on the table.
		 * @type {Object}
		 */
		no_data: {
			type: String,
			default: "No Data Available."
		},
		/**
		 * method to call to set the row css class.
		 * @type {Object}
		 */
		row_class: {
			type: null
		},
		/**
		 * action Field Settings
		 * @type {Object}
		 */
		actions:{
			type: Object,
			default: () => ({
				ignore: true,
				id_field: 'id',
				view_path:'',
				edit_path:'',
				delete_path:'',
                view_modal:null,
                edit_modal:null,
                delete_modal:null
			})
		},
        multi_select: false,
        unsortable: {
		    type:Boolean,
            default: false
        },
        search_on_start: {
            type:Boolean,
            default: true
        },
        /**
         * name of setting storage location; saves data so
         * reloads last dataset and page used
         * */
        storageLocation: {
            type: String,
            default: null
        },
	},
	// components to use in template
	components:{
		Pagination,
		DataTableCell,
		DataTableActions,
        DataTableSelect
	},
	// component data
	data() {
		return {
			filtered_results: [],
			list_rows: [],
			// array to store sorted data
			sorted_data: [],
			// sorting object for table.
			sorting:{
			current: '',
			direction: 'asc'
			},
			pagination:{
				page:1,
				limit:30,
				count: 0
			},
			local_data: [],
			search_term: "",
			searchable_columns: [],
			useAPI: false,
			typeCount: 0,
			useActions: false,
            customQString: false,
            selectedRows: [],
            allSelected: false,
            currentWidth: 0,
            preSearchFunction: null,
            callBackFunction: null,
            isLoading: false,
		};
	},
	// component was mounted
	mounted(){
	    this.pagination.limit = this.per_page;
		this.initialize();
	},
	// any methods needed for this component
	methods: {
		initialize(){
			let counter = 0,
				searchable = [];
			for(let field in this.table_headers){
				// sort by first item in headers
				if(counter == 0) this.sorting.current = this.table_headers[field].sortBy ? this.table_headers[field].sortBy:field;
				// check if column is searchable
				const is_searchable = (this.table_headers[field].searchable != undefined)? this.table_headers[field].searchable : true;
				if(is_searchable) searchable.push(field);
				// check if column is the default sorting
				if(this.table_headers[field].default_sort != undefined){
					this.sorting = {
						current: this.table_headers[field].sortBy ? this.table_headers[field].sortBy:field,
						direction: this.table_headers[field].default_sort
					}
				}
				counter ++;
			}
			this.searchable_columns = searchable;

			if(this.api_point != ""){
				this.useAPI = true;
				this.search_on_start ? this.loadStorage():null;
			} else {
				this.initializeTableData();
			}

			if(!this.actions.ignore){
				this.useActions = true;
			}
		},
		/**
		 * create copies of table data
		 * @return {[type]} [description]
		 */
		initializeTableData(){
			this.local_data = _.cloneDeep(this.table_data);
			this.createRenderedData();
			this.sortTableData();
			if(this.size_content){
			    this.$refs.table.style.width = this.tableWidthByContent();
            }
		},
		headerClass(field,header){
			let sortable = (header.sortable == undefined)? true : header.sortable;
			let string = "";
			if(sortable) string += " sortable";
			if((this.sorting.current == field || this.sorting.current == this.table_headers[field].sortBy)
				&& !this.unsortable){
				string += ` sorting ${this.sorting.direction}`;
			}
			return string;
		},
		/**
		 * get display value from header
		 * if not provided use field value.
		 * @param  {[type]} header [description]
		 * @param  {[type]} field  [description]
		 * @return {[type]}        [description]
		 */
		columnHeaderDisplay(header,field){
			if(header.display != undefined) return header.display;
			return field.unSlug().ucfirst();
		},
		/**
		* sort table data using settings in this.sorting
		* @return {[type]} [description]
		*/
		sortTableData(){
			if(this.useAPI) {
				this.goToPage(this.pagination.page);
				return;
			}

			let sorted = _sortBy(this.filtered_results,[this.sorting.current]);
			if(this.sorting.current && this.table_headers[this.sorting.current] && this.table_headers[this.sorting.current].sortable){
                this.sorted_data = (this.sorting.direction == 'asc')? sorted : sorted.reverse();
            }
		},
		/**
		* header clicked to sort
		* if same switch direction.
		* @param  {string} header sorting header
		* @return {void}
		*/
		sortBy(header, field){
			if(header.sortable == false) return;
			let direction = 'asc';
			let sortField = field;
			if(header.sortBy !== undefined){
				sortField = header.sortBy;
			}
			if(sortField == this.sorting.current){
				direction = this.sorting.direction == 'asc'? 'desc' : 'asc';
			}
			this.sorting = {
				current: sortField,
				direction: direction
			};
			this.sortTableData();
		},
		searchTable(){
			// no term
			if(this.search_term.length == 0){
				this.filtered_results = _.cloneDeep(this.local_data);
			}else{
				// search
				const search = this.search_term.toLowerCase().replace(/\s/g, '');
				// check against searchable fields
				// eslint-disable-next-line
				this.filtered_results = _.filter(this.local_data, (row) => {
					let match = false;
					for(let i in this.searchable_columns){
						let column = this.searchable_columns[i];
						let value = null;
						try {
							// eslint-disable-next-line
							value = eval(`row.${column}`);
						}catch(err){
							//console.log(err);
							value = null;
						}
						// eslint-disable-next-line
						console.log(`value ${value}`);
						if(value == null) continue;
						match = value.toLowerCase().indexOf(search) !== -1;
						if(match) break;
					}
					return match;
				});
			}
			this.pagination.page = 1;
			this.sortTableData();
		},
        setPreSearchCallback(fn){
		    this.preSearchFunction = fn;
        },
        setSearchCallback(fn){
		    this.callBackFunction = fn;
        },
		apiSearch(){
			this.typeCount--;
			if(this.typeCount <= 0){
				this.startSearch();
			}
		},
		startSearch(){
            if(typeof this.preSearchFunction == "function"){
                this.preSearchFunction();
            }
		    if(this.customQString && typeof this.customQString != "string"){
		        this.customQString.sort =  this.sorting.current;
		        this.customQString.dir = this.sorting.direction;
		        this.customQString.limit = this.pagination.limit;
		        this.isLoading = true;
                axios.get("/api/" + this.api_point, {params: this.customQString}).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            }
		    else if(this.unsortable){
		        this.isLoading = true;
                axios.get("/api/" + this.api_point + "?q=" + this.search_term + "&limit=" + this.pagination.limit).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            }
		    else {
		        this.isLoading = true;
                axios.get("/api/" + this.api_point + "?q=" + this.search_term + "&sort=" + this.sorting.current + "&dir=" + this.sorting.direction + "&limit=" + this.pagination.limit).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            }
		},

		loadPage(i){
            if(this.customQString && typeof this.customQString != "string"){
                this.customQString.sort =  this.sorting.current;
                this.customQString.dir = this.sorting.direction;
                this.customQString.limit = this.pagination.limit;
                this.customQString.page = i;
                this.isLoading = true;
                axios.get("/api/" + this.api_point, {params: this.customQString}).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            }
            else if(this.unsortable){
                this.isLoading = true;
                axios.get("/api/" + this.api_point + "?q=" + this.search_term + "&limit=" + this.pagination.limit + "&page=" + i).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            } else {
                this.isLoading = true;
                axios.get("/api/" + this.api_point + "?q=" + this.search_term + "&sort=" + this.sorting.current + "&dir=" + this.sorting.direction + "&page=" + i + "&limit=" + this.pagination.limit).then((ax) => this.gotData(ax))
                    .catch(error => this.dataFailure(error));
            }
		},

        startCustomSearch(qString){
		    this.customQString = qString;
		    this.startSearch();
        },
		// event listener when user switches pages
        goToPage(page){
			if(this.useAPI){
				this.loadPage(page);
			}else{
				this.pagination.page = page;
				this.paginate();
			}
        },
		refreshPage(){
			this.goToPage(this.pagination.page);
		},
		//update content from data
		gotData(dt){
		    this.isLoading = false;
			if(dt.data && dt.data.success){
				this.list_rows = dt.data.data;
				if(dt.data.paged){
					this.pagination.page = dt.data.pagination.page;
					this.pagination.limit = dt.data.pagination.limit;
					this.pagination.count = dt.data.pagination.total;
				}
				this.saveStorage();
			}
			if(typeof this.callBackFunction == "function"){
			    this.callBackFunction();
            }
		},
        dataFailure(error){
            this.isLoading = false;
            console.warn("data failed to load.", error);
            if(typeof this.callBackFunction == "function"){
                this.callBackFunction();
            }
        },
        /**
         * splice results list and only show current page results.
         * @return void
         */
        paginate(){
            var index = (this.pagination.page - 1) * this.pagination.limit;
            var copy = _.cloneDeep(this.sorted_data);
			if(copy.length == 0){
				copy = _.cloneDeep(this.filtered_results);
			}
            this.list_rows = copy.splice(index,this.pagination.limit);
        },
        /**
         * check if row_class is set
         * if so call it
         * @param  {[type]} row_data [description]
         * @return {[type]}          [description]
         */
        rowClass(row_data){
			if(this.row_class == null) return "";
			return this.row_class(row_data);
        },
        /**
         * create array of all rendered data.
         * @return {[type]} [description]
         */
        createRenderedData(){
			for(let i in this.local_data){
				for(let field in this.table_headers){
					let header = this.table_headers[field];
					if(header.render != undefined && header.searchable !== false){
						this.local_data[i][field] = header.render(this.local_data[i]);
					}
				}
			}
			this.filtered_results = _.cloneDeep(this.local_data);
			this.list_rows = _.cloneDeep(this.local_data);
		},
        clearSelections(){
            this.selectedRows = [];
            let boxes = this.$el.querySelectorAll("input[type=checkbox]");
            for(let i = 0; i < boxes.length;++i){
                boxes[i].checked = false
            }
        },
        columnWidthByContent(field) {
            if(typeof this.table_data[0] !== 'undefined') {
                let longest = 0;
                let max = 300;
                let title = field.unSlug ? field.unSlug().ucfirst():field;
                if(typeof this.table_headers[field].width !== 'undefined') {
                    return this.table_headers[field].width;
                }
                if(typeof this.table_data[0][field] == 'undefined') {
                    return 100; // Assume a moderate sized field
                }
                for(let i = 0; i < this.table_data.length; i++) {
                    let contentLength = String(this.table_data[i][field]).length;
                    if(contentLength > longest) {
                        longest = contentLength;
                    }
                }
                if(typeof this.table_headers[field].display !== 'undefined') {
                    title = this.table_headers[field].display;
                }
                let titleWords = title.split(' ');
                for(let word of titleWords) {
                    longest = (longest > word.length) ? longest : word.length;
                }
                let len = (60 + (longest * 8));
                return (len > max) ? max : len;
            }
        },
        tableWidthByContent() {
            this.resize();
            let width = 0;
            for(let field in this.table_headers) {
                let columnWidth = this.columnWidthByContent(field);
                width += columnWidth;
            }
            width = (width > this.currentWidth) ? width : this.currentWidth;
            return width + 'px';
        },
        resize() {
            this.currentWidth = this.$el.clientWidth;
        },
        loadStorage(){
            if(this.storageLocation){
                if(GlobalStorage.has(this.storageLocation)){
                    let storedData = GlobalStorage.load(this.storageLocation);
                    this.sorting = storedData.sorting ? storedData.sorting:this.sorting;
                    this.customQString = storedData.customQString ? storedData.customQString:storedData.customQString;
                    this.search_term = storedData.search_term ? storedData.search_term:this.search_term;
                    this.pagination = storedData.pagination ? storedData.pagination:this.pagination;
                }

                this.pagination.page == 1 ? this.startSearch():this.goToPage(this.pagination.page);
            }else{
                this.startSearch();
            }
        },
        saveStorage(){
            if(this.storageLocation){
                let storeData = {
                    sorting: this.sorting,
                    customQString: this.customQString ? this.customQString:null,
                    search_term: this.search_term,
                    pagination: this.pagination
                };

                GlobalStorage.save(this.storageLocation, storeData);
            }
        },
		clearStorage(){
        	if(this.storageLocation){
        		let firstSortable = null;
				for(let field in this.table_headers){
					if(this.table_headers[field].sortable !== false){
						firstSortable = this.table_headers[field].sortBy ? this.table_headers[field].sortBy:field;
						break;
					}
				}
				this.sorting = {
					current: firstSortable,
					direction: 'asc'
				};
				this.customQString = null;
				this.search_term = "";
				this.pagination = {
					page:1,
					limit:30,
					count: 0
				};
				GlobalStorage.save(this.storageLocation, null);
				this.startSearch();
			}
		}
	},
	// computed properties
	computed:{
		totalHeaders(){
			let total = 0;
			//eslint-disable-next-line
			for(let i in this.table_headers){
				total++;
			}
			if(this.useActions || this.multi_select){
				total++;
			}
			return total;
		},

		actionIdField(){
			if(this.actions.id_field){
				return this.actions.id_field;
			}

			return "id";
		}
	},
	// property watchers
	watch:{
		table_data(){
			// create a local copy of the table data.
			this.initializeTableData();
		},
		search_term(){
			if(this.useAPI){
				this.typeCount++;
				setTimeout(() => this.apiSearch(), 400);
			}else{
				this.searchTable();
			}

		},
        per_page(){
		    this.pagination.limit = this.per_page;
        }
	}
}
</script>
